import { Icon, SearchInput, Text } from "@assetwatch/ui";
import { useState } from "react";
import { getAncestorPath } from "../../../lib/assetPickerTree";
import type { AssetPickerOption } from "../../../queries/useAssetPickerQuery";
import { useAssetPickerQuery } from "../../../queries/useAssetPickerQuery";
import type { WorkRequestAssetCandidate } from "../../../types/workRequest";
import type { IntakeChatEntry } from "./useNewWorkRequestIntake";

export function AssistantMessage({ children }: { children: string }) {
  return (
    <div className="flex items-start gap-(--spacing-10)">
      <span className="mt-(--spacing-2) inline-flex size-(--spacing-32) shrink-0 items-center justify-center rounded-full bg-primary text-primary-on-primary">
        <Icon name="sparkles" size="sm" tone="inherit" />
      </span>
      <div className="max-w-[calc(100%-var(--spacing-40))] rounded-(--radius-lg) bg-surface px-(--spacing-16) py-(--spacing-12) shadow-elevation-1">
        <Text>{children}</Text>
      </div>
    </div>
  );
}

export function AssistantTypingIndicator() {
  return (
    <output
      aria-label="Assistant is responding"
      className="flex items-start gap-(--spacing-10)"
    >
      <span className="mt-(--spacing-2) inline-flex size-(--spacing-32) shrink-0 items-center justify-center rounded-full bg-primary text-primary-on-primary">
        <Icon name="sparkles" size="sm" tone="inherit" />
      </span>
      <div className="flex items-center gap-(--spacing-8) rounded-(--radius-lg) bg-surface px-(--spacing-16) py-(--spacing-12) shadow-elevation-1">
        <span className="aw-spinner inline-flex" aria-hidden="true">
          <Icon name="button-load-spinner" size="sm" tone="secondary" />
        </span>
        <Text tone="subtle">Thinking…</Text>
      </div>
    </output>
  );
}

export function UserMessage({ children }: { children: string }) {
  return (
    <div className="flex justify-end">
      <p className="max-w-[82%] rounded-(--radius-lg) bg-surface-inverse-surface px-(--spacing-16) py-(--spacing-12) font-(family-name:--font-family-body) text-(length:--font-size-body-large) leading-(--font-line-height-24) text-surface-inverse-on-surface">
        {children}
      </p>
    </div>
  );
}

function AssetMessage({ assetName }: { assetName: string }) {
  return (
    <div className="flex justify-end">
      <div className="flex max-w-[82%] items-center gap-(--spacing-10) rounded-(--radius-lg) bg-primary-primary-container px-(--spacing-16) py-(--spacing-10)">
        <span className="size-(--spacing-8) shrink-0 rounded-full bg-warning" />
        <span className="min-w-0">
          <span className="block truncate font-(family-name:--font-family-label) text-(length:--font-size-body-large) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default">
            {assetName}
          </span>
        </span>
      </div>
    </div>
  );
}

export function getReviewAssetLabel(
  entries: IntakeChatEntry[],
  fallback: string | null,
) {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (entry.role === "asset") return entry.assetName;
  }
  return fallback;
}

function indicatorClass(score: number) {
  if (score >= 85) return "bg-positive";
  if (score >= 70) return "bg-warning";
  return "bg-negative";
}

function getAssetParentPath(
  candidate: WorkRequestAssetCandidate,
  allAssets: AssetPickerOption[],
) {
  if (candidate.hierarchyPath) {
    const names = candidate.hierarchyPath.split(" > ").filter(Boolean);
    if (names.at(-1) === candidate.assetName) names.pop();
    return names.join(" > ");
  }
  return getAncestorPath(allAssets, candidate.assetId);
}

export function AssetCandidates({
  candidates,
  disabled,
  onSelect,
}: {
  candidates: WorkRequestAssetCandidate[];
  disabled: boolean;
  onSelect: (candidate: WorkRequestAssetCandidate) => void;
}) {
  const [search, setSearch] = useState("");
  const { data: pickerData } = useAssetPickerQuery();
  const allAssets = pickerData?.options ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const candidateByAssetId = new Map(
    candidates.map((candidate) => [candidate.assetId, candidate]),
  );
  const searchedAssets = normalizedSearch
    ? allAssets.filter(
        (asset) =>
          asset.name.toLowerCase().includes(normalizedSearch) ||
          (asset.assetNumber ?? "").toLowerCase().includes(normalizedSearch) ||
          asset.id.toLowerCase().includes(normalizedSearch),
      )
    : [];
  const visibleCandidates: WorkRequestAssetCandidate[] =
    searchedAssets.length > 0
      ? searchedAssets.map((asset) => ({
          ...candidateByAssetId.get(asset.id),
          assetId: asset.id,
          assetName: asset.name,
          assetCode: asset.assetNumber,
          kind: asset.kind,
          score: 100,
          reason: "direct facility asset search",
        }))
      : candidates.filter(
          (candidate) =>
            normalizedSearch.length === 0 ||
            candidate.assetName.toLowerCase().includes(normalizedSearch) ||
            candidate.assetCode?.toLowerCase().includes(normalizedSearch) ||
            candidate.assetId.toLowerCase().includes(normalizedSearch),
        );

  return (
    <div className="ml-(--spacing-40) rounded-(--radius-sm) bg-surface p-(--spacing-16) shadow-elevation-1">
      <SearchInput
        aria-label="Search assets"
        debounceMs={0}
        disabled={disabled}
        onChange={setSearch}
        placeholder="Search by name or ID..."
        value={search}
      />
      <div className="mt-(--spacing-10) overflow-hidden rounded-(--radius-sm) border border-outline-subtle">
        {visibleCandidates.map((candidate) => (
          <button
            className="flex w-full items-center gap-(--spacing-10) border-b border-outline-subtle bg-surface px-(--spacing-16) py-(--spacing-10) text-left outline-none last:border-b-0 hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button-focus-ring disabled:pointer-events-none disabled:opacity-40"
            disabled={disabled}
            key={candidate.assetId}
            onClick={() => onSelect(candidate)}
            type="button"
          >
            <span
              className={`size-(--spacing-8) shrink-0 rounded-full ${indicatorClass(candidate.score)}`}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-(family-name:--font-family-label) text-(length:--font-size-body-medium) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default">
                {candidate.assetName}
              </span>
              {getAssetParentPath(candidate, allAssets) ? (
                <span className="block truncate font-(family-name:--font-family-label) text-(length:--font-size-body-small) leading-(--font-line-height-16) text-text-subtle">
                  {getAssetParentPath(candidate, allAssets)}
                </span>
              ) : null}
            </span>
            <Icon name="chevron-right" size="sm" tone="secondary" />
          </button>
        ))}
        {visibleCandidates.length === 0 ? (
          <div className="px-(--spacing-16) py-(--spacing-20) text-center">
            <Text tone="subtle">No matching assets in these suggestions.</Text>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function IntakeTranscript({ entries }: { entries: IntakeChatEntry[] }) {
  return (
    <>
      {entries.map((entry, index) => {
        const key = `${entry.role}-${index}`;
        if (entry.role === "assistant") {
          return <AssistantMessage key={key}>{entry.content}</AssistantMessage>;
        }
        if (entry.role === "asset") {
          return <AssetMessage assetName={entry.assetName} key={key} />;
        }
        return <UserMessage key={key}>{entry.content}</UserMessage>;
      })}
    </>
  );
}
